// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    getPageLinkByNodeId,
    LinkModel,
    useGetLink,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomFieldDefinition } from "@vanmeter/CustomFieldDefinition";
import xIconsObject from "@vanmeter/XDL/Icons/xngCommonIcons";
import * as cssLinter from "css";
import * as React from "react";
import { FC } from "react";
import { css } from "styled-components";

const enum fields {
    direction = "direction",
    alignment = "alignment",
    title = "title",
    titleLink = "titleLink",
    links = "links",
    customCSS = "customCSS",
    // XNG-Changes
    titleColor = "titleColor",
    linkColor = "linkColor",
    gapBetweenLinks = "gapBetweenLinks",
}

// XNG-Changes
export interface XLinkModel extends LinkModel {
    icon?: string;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.direction]: "vertical" | "horizontal";
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        // XNG-Changes: change type
        [fields.links]: XLinkModel[];
        [fields.customCSS]: string;
        // XNG-Changes
        [fields.titleColor]: string;
        [fields.linkColor]: string;
        [fields.gapBetweenLinks]: number;
    };
    extendedStyles?: LinkListStyles;
}

export interface LinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    // XNG-Changes
    listLink?: LinkPresentationProps;
    titleLink?: LinkPresentationProps;
}

export const linkListStyles: LinkListStyles = {
    linkListWrapper: {
        css: css`
            text-transform: uppercase;
            padding: 10px 0;
        `,
    },
    titleWrapper: {
        css: css`
            font-weight: bold;
        `,
    },
};

const defaultCustomCss = `.list-wrapper {
}

.title-wrapper {
}

.list-title {
}

.links-wrapper {
}

.list-link {
}
`;

export const LinkList: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { links, hasMissingCategories } = useGetLinks(fields.links, o => o.fields.destination);
    const titleLink = useGetLink(fields.titleLink);
    const styles = useMergeStyles("linkList", linkListStyles, extendedStyles);
    if (hasMissingCategories) {
        return null;
    }

    // XNG-Changes
    const alignmentStyles = {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: ${fields.alignment || "flex-end"};
            ${styles.linkListWrapper?.css}
        `,
    };

    // XNG-Changes
    const directionStyles = {
        css: css`
            display: flex;
            ${fields.direction === "horizontal"
                ? `
                    flex-direction: row;
                    column-gap: ${fields.gapBetweenLinks}px;
                `
                : `flex-direction: column;
                    row-gap: ${fields.gapBetweenLinks}px;
                `}
            ${styles.linkWrapper?.css}
        `,
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper} className="link-list">
            <StyledWrapper className="list-wrapper" {...alignmentStyles}>
                <StyledWrapper className="title-wrapper" {...styles.titleWrapper}>
                    {fields.title &&
                        (titleLink?.url ? (
                            <Link
                                className="list-title"
                                href={titleLink.url}
                                // XNG-Changes
                                color={fields.titleColor}
                                aria-label={fields.title || titleLink.title}
                                {...styles.titleLink}
                            >
                                {fields.title || titleLink.title}
                            </Link>
                        ) : (
                            <Typography
                                className="list-title"
                                // as="h3"
                                {...styles.title}
                                // XNG-Changes
                                color={fields.titleColor}
                            >
                                {fields.title}
                            </Typography>
                        ))}
                </StyledWrapper>
                <StyledWrapper className="links-wrapper" {...directionStyles}>
                    {links.map(
                        (link, index) =>
                            link.url && (
                                <Link
                                    className="list-link"
                                    href={link.url}
                                    target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                    // XNG-Changes
                                    {...styles.listLink}
                                    color={fields.linkColor}
                                    key={`${link.url}.${index}`}
                                    aria-label={fields.links[index].fields.overriddenTitle || link.title}
                                    {...{
                                        icon: {
                                            iconProps: {
                                                src:
                                                    xIconsObject[(fields.links[index].fields as any)["icon"]] ||
                                                    undefined,
                                                color: fields.linkColor,
                                                size: 24,
                                            },
                                        },
                                    }}
                                >
                                    {xIconsObject[(fields.links[index].fields as any)["icon"]]
                                        ? ""
                                        : fields.links[index].fields.overriddenTitle || link.title}
                                </Link>
                            ),
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

// XNG-Changes
const styleTab = {
    displayName: "Style",
    sortOrder: 1,
};

const widgetModule: WidgetModule<CustomFieldDefinition> = {
    component: LinkList,
    definition: {
        group: "Basic",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.direction,
                editorTemplate: "DropDownField",
                defaultValue: "vertical",
                fieldType: "General",
                options: [
                    {
                        displayName: "Vertical",
                        value: "vertical",
                    },
                    {
                        displayName: "Horizontal",
                        value: "horizontal",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        // XNG-Changes
                        value: "flex-start",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        // XNG-Changes
                        value: "flex-end",
                    },
                ],
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    // XNG-Changes
                    {
                        name: "icon",
                        editorTemplate: "IconField" as any,
                        defaultValue: "",
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item: any, shouldDisplayAdvancedFeatures: any) => !!shouldDisplayAdvancedFeatures,
                validate: (value: any) => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            // XNG-Changes
            {
                name: fields.titleColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: styleTab,
            },
            {
                name: fields.linkColor,
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: styleTab,
            },
            {
                name: fields.gapBetweenLinks,
                displayName: "Gap Between Links",
                editorTemplate: "IntegerField",
                defaultValue: 24,
                fieldType: "General",
                tab: styleTab,
            },
        ],
    },
};

export default widgetModule;
